import { useEffect, useState } from 'react';
import { Library } from './pages/Library.tsx';
import { Intake } from './pages/Intake.tsx';
import { Editor } from './pages/Editor.tsx';
import { Settings } from './pages/Settings.tsx';
import { Usage } from './pages/Usage.tsx';
import { Topbar } from './components/Topbar.tsx';
import { UpgradeBanner } from './components/UpgradeBanner.tsx';
import { api, FetchError } from './api.ts';
import type { Proposal } from '@sab/core/schemas';

type Route =
  | { name: 'library' }
  | { name: 'intake' }
  | { name: 'editor'; id: string }
  | { name: 'settings' }
  | { name: 'usage' };

function readRoute(): Route {
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (hash.startsWith('editor/')) {
    return { name: 'editor', id: hash.slice('editor/'.length) };
  }
  if (hash === 'new') return { name: 'intake' };
  if (hash === 'settings') return { name: 'settings' };
  if (hash === 'usage') return { name: 'usage' };
  return { name: 'library' };
}

export function App() {
  const [route, setRoute] = useState<Route>(readRoute);
  const [me, setMe] = useState<{
    email: string;
    environment: string;
    publicBaseUrl: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((err: unknown) => {
        const msg = err instanceof FetchError ? err.body.error : 'Auth gagal';
        setError(msg);
      });
  }, []);

  const goto = (path: string) => {
    window.location.hash = path;
  };

  const handleCreated = (p: Proposal) => {
    goto(`/editor/${p.id}`);
  };

  return (
    <div className="app">
      <Topbar
        userEmail={me?.email ?? null}
        env={me?.environment ?? 'unknown'}
        onHome={() => goto('/')}
      />

      {error ? (
        <div className="app__error" role="alert">
          {error}
        </div>
      ) : null}

      <UpgradeBanner />

      <main className="app__main">
        {route.name === 'library' ? (
          <Library
            onOpen={(id) => goto(`/editor/${id}`)}
            onNew={() => goto('/new')}
            publicBaseUrl={me?.publicBaseUrl ?? 'https://view.sab.id'}
          />
        ) : null}
        {route.name === 'intake' ? <Intake onCreated={handleCreated} /> : null}
        {route.name === 'editor' ? (
          <Editor
            proposalId={route.id}
            publicBaseUrl={me?.publicBaseUrl ?? 'https://view.sab.id'}
          />
        ) : null}
        {route.name === 'settings' ? <Settings /> : null}
        {route.name === 'usage' ? <Usage /> : null}
      </main>
    </div>
  );
}
